// /opt/isp_erp/frontend/src/pages/admin/finanzas/FacturaDetalle.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { facturasApi } from '../../../api/finanzas/facturas.api';
import './FacturaDetalle.css';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';

export default function FacturaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [factura, setFactura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    observaciones: '',
    fecha_vencimiento: '',
    fecha_emision: '',
    estado: '',
    subtotal: 0,
    descuento: 0,
    iva: 0
  });

  useEffect(() => {
    cargarFactura();
  }, [id]);

  const cargarFactura = async () => {
    try {
      const response = await facturasApi.obtener(id);
      const data = response.data;
      setFactura(data);
      
      // Manejar fechas correctamente
      let fechaVencimiento = '';
      let fechaEmision = '';
      
      if (data.fecha_vencimiento) {
        const fechaObj = new Date(data.fecha_vencimiento);
        if (!isNaN(fechaObj.getTime())) {
          fechaVencimiento = fechaObj.toISOString().split('T')[0];
        }
      }
      
      if (data.fecha_emision) {
        const fechaObj = new Date(data.fecha_emision);
        if (!isNaN(fechaObj.getTime())) {
          fechaEmision = fechaObj.toISOString().split('T')[0];
        }
      }
      
      setEditData({
        observaciones: data.observaciones || '',
        fecha_vencimiento: fechaVencimiento,
        fecha_emision: fechaEmision,
        estado: data.estado || 'PENDIENTE',
        subtotal: data.subtotal || 0,
        descuento: data.descuento || 0,
        iva: data.iva || 0
      });
    } catch (error) {
      console.error('Error cargando factura:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerarPDF = async () => {
    try {
      const response = await facturasApi.generarPDF(id);
      
      // Crear un blob a partir de la respuesta
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Crear URL para el blob
      const url = window.URL.createObjectURL(blob);
      
      // Abrir en nueva ventana
      window.open(url, '_blank');
      
      // Limpiar URL después de un tiempo
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el PDF: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAnular = async () => {
    if (!window.confirm('¿Está seguro de anular esta factura?')) return;

    const motivo = prompt('Motivo de anulación:');
    if (!motivo) return;

    try {
      await facturasApi.anular(id, motivo);
      alert('✅ Factura anulada correctamente');
      cargarFactura();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al anular');
    }
  };

  const handleEliminar = async () => {
    if (!window.confirm('⚠️ ¿Está SEGURO de eliminar permanentemente esta factura?\n\nEsta acción no se puede deshacer.')) return;

    const motivo = prompt('Motivo de eliminación:');
    if (!motivo) return;

    try {
      await facturasApi.eliminar(id, motivo);
      alert('✅ Factura eliminada permanentemente');
      navigate('/finanzas/facturas');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleGuardarCambios = async () => {
    try {
      // Preparar datos para actualizar
      const datosActualizar = {};
      
      if (editData.observaciones !== factura.observaciones) {
        datosActualizar.observaciones = editData.observaciones;
      }
      
      if (editData.fecha_vencimiento !== (factura.fecha_vencimiento?.split('T')[0])) {
        datosActualizar.fecha_vencimiento = editData.fecha_vencimiento;
      }
      
      if (editData.fecha_emision !== (factura.fecha_emision?.split('T')[0])) {
        datosActualizar.fecha_emision = editData.fecha_emision;
      }
      
      if (editData.estado !== factura.estado) {
        datosActualizar.estado = editData.estado;
      }
      
      if (parseFloat(editData.subtotal) !== factura.subtotal) {
        datosActualizar.subtotal = parseFloat(editData.subtotal);
        // Recalcular total si cambia subtotal
        const nuevoTotal = parseFloat(editData.subtotal) - parseFloat(editData.descuento) + parseFloat(editData.iva);
        datosActualizar.total = nuevoTotal;
      }
      
      if (parseFloat(editData.descuento) !== factura.descuento) {
        datosActualizar.descuento = parseFloat(editData.descuento);
        // Recalcular total si cambia descuento
        const nuevoTotal = parseFloat(editData.subtotal) - parseFloat(editData.descuento) + parseFloat(editData.iva);
        datosActualizar.total = nuevoTotal;
      }
      
      if (parseFloat(editData.iva) !== factura.iva) {
        datosActualizar.iva = parseFloat(editData.iva);
        // Recalcular total si cambia IVA
        const nuevoTotal = parseFloat(editData.subtotal) - parseFloat(editData.descuento) + parseFloat(editData.iva);
        datosActualizar.total = nuevoTotal;
      }
      
      if (Object.keys(datosActualizar).length === 0) {
        alert('No hay cambios para guardar');
        return;
      }
      
      await facturasApi.actualizar(id, datosActualizar);
      alert('✅ Factura actualizada correctamente');
      setEditMode(false);
      cargarFactura();
    } catch (error) {
      console.error('Error al actualizar:', error);
      alert(error.response?.data?.detail || 'Error al actualizar la factura');
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    try {
      // Crear objeto Date directamente con la fecha ISO
      let fechaObj = new Date(fecha);
      
      // Verificar si la fecha es válida
      if (isNaN(fechaObj.getTime())) {
        console.warn('Fecha inválida:', fecha);
        return 'Fecha no disponible';
      }
      
      // Formatear a dd/mm/yyyy
      const dia = fechaObj.getDate().toString().padStart(2, '0');
      const mes = (fechaObj.getMonth() + 1).toString().padStart(2, '0');
      const anio = fechaObj.getFullYear();
      
      return `${dia}/${mes}/${anio}`;
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Fecha inválida';
    }
  };

  if (loading) {
    return (
      <div className="factura-loading-container">
        <div className="factura-loading-spinner"></div>
        <p>Cargando factura...</p>
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="factura-error-container">
        <div className="factura-error-card">
          <div className="error-icon">❌</div>
          <h3>Factura no encontrada</h3>
          <p>La factura que buscas no existe o ha sido eliminada</p>
          <button
            onClick={() => navigate('/finanzas/facturas')}
            className="factura-btn-primary"
          >
            ← Volver a Facturas
          </button>
        </div>
      </div>
    );
  }

  const puedeEditar = factura.estado === 'PENDIENTE';
  const puedeAnular = factura.estado === 'PENDIENTE';
  const puedeEliminar = factura.estado === 'PENDIENTE' || factura.estado === 'ANULADA';

  return (
    <div className="factura-detalle">
      {/* Header */}
      <div className="factura-header">
        <div className="factura-header-left">
          <div className="factura-header-icon">📄</div>
          <div>
            <h1>Factura {factura.numero_factura}</h1>
            <div className="factura-cliente-info">
              <p className="cliente-nombre">
                {factura.customer?.nombres} {factura.customer?.apellidos}
              </p>
              <span className="cliente-id">ID: {factura.customer?.identificacion}</span>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="factura-actions">
          {puedeEditar && !editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="factura-btn-edit"
              title="Editar factura"
            >
              ✏️ Editar
            </button>
          )}

          {editMode && (
            <>
              <button
                onClick={() => setEditMode(false)}
                className="factura-btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardarCambios}
                className="factura-btn-save"
              >
                💾 Guardar
              </button>
            </>
          )}

          {puedeAnular && !editMode && (
            <button
              onClick={handleAnular}
              className="factura-btn-cancel"
              title="Anular factura"
            >
              ❌ Anular
            </button>
          )}

          {puedeEliminar && !editMode && (
            <button
              onClick={handleEliminar}
              className="factura-btn-delete"
              title="Eliminar permanentemente"
            >
              🗑️ Eliminar
            </button>
          )}


          {/* ✅ Botón de PDF */}
          <button
            onClick={handleGenerarPDF}
            className="factura-btn-pdf"
            title="Generar PDF"
          >
            📄 PDF
          </button>

          <button
            onClick={() => navigate('/finanzas/facturas')}
            className="factura-btn-back"
          >
            ← Volver
          </button>
        </div>
      </div>

      {/* Badge de estado */}
      <div className="factura-status">
        <span className={`status-badge status-${factura.estado?.toLowerCase()}`}>
          <span className="status-dot">●</span>
          {factura.estado}
        </span>
      </div>

      {/* Datos de la factura */}
      <div className="factura-info-grid">
        {editMode ? (
          <>
            <div className="info-card editable">
              <div className="info-icon">📅</div>
              <div className="info-editable">
                <p className="info-label">Fecha Emisión *</p>
                <input
                  type="date"
                  value={editData.fecha_emision}
                  onChange={(e) => setEditData({...editData, fecha_emision: e.target.value})}
                  className="info-input"
                />
              </div>
            </div>

            <div className="info-card editable">
              <div className="info-icon">⏰</div>
              <div className="info-editable">
                <p className="info-label">Fecha Vencimiento *</p>
                <input
                  type="date"
                  value={editData.fecha_vencimiento}
                  onChange={(e) => setEditData({...editData, fecha_vencimiento: e.target.value})}
                  className="info-input"
                  min={editData.fecha_emision}
                />
              </div>
            </div>

            <div className="info-card editable">
              <div className="info-icon">📊</div>
              <div className="info-editable">
                <p className="info-label">Estado</p>
                <select
                  value={editData.estado}
                  onChange={(e) => setEditData({...editData, estado: e.target.value})}
                  className="info-select"
                >
                  <option value="PENDIENTE">PENDIENTE</option>
                  <option value="PAGADA">PAGADA</option>
                  <option value="ANULADA">ANULADA</option>
                  <option value="VENCIDA">VENCIDA</option>
                </select>
              </div>
            </div>

            <div className="info-card editable">
              <div className="info-icon">💰</div>
              <div className="info-editable">
                <p className="info-label">Subtotal</p>
                <input
                  type="number"
                  step="0.01"
                  value={editData.subtotal}
                  onChange={(e) => {
                    const subtotal = parseFloat(e.target.value) || 0;
                    setEditData({...editData, subtotal});
                  }}
                  className="info-input"
                />
              </div>
            </div>

            <div className="info-card editable">
              <div className="info-icon">🏷️</div>
              <div className="info-editable">
                <p className="info-label">Descuento</p>
                <input
                  type="number"
                  step="0.01"
                  value={editData.descuento}
                  onChange={(e) => {
                    const descuento = parseFloat(e.target.value) || 0;
                    setEditData({...editData, descuento});
                  }}
                  className="info-input"
                />
              </div>
            </div>

            <div className="info-card editable">
              <div className="info-icon">📈</div>
              <div className="info-editable">
                <p className="info-label">IVA</p>
                <input
                  type="number"
                  step="0.01"
                  value={editData.iva}
                  onChange={(e) => {
                    const iva = parseFloat(e.target.value) || 0;
                    setEditData({...editData, iva});
                  }}
                  className="info-input"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="info-card">
              <div className="info-icon">📅</div>
              <div>
                <p className="info-label">Fecha Emisión</p>
                <p className="info-value">{formatearFecha(factura.fecha_emision)}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">⏰</div>
              <div>
                <p className="info-label">Fecha Vencimiento</p>
                <p className="info-value">{formatearFecha(factura.fecha_vencimiento)}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">📋</div>
              <div>
                <p className="info-label">Tipo</p>
                <p className="info-value">{factura.tipo}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">📊</div>
              <div>
                <p className="info-label">Estado</p>
                <p className="info-value">{factura.estado}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">💰</div>
              <div>
                <p className="info-label">Subtotal</p>
                <p className="info-value">${factura.subtotal?.toFixed(2)}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">🏷️</div>
              <div>
                <p className="info-label">Descuento</p>
                <p className="info-value">${factura.descuento?.toFixed(2)}</p>
              </div>
            </div>

            <div className="info-card">
              <div className="info-icon">📈</div>
              <div>
                <p className="info-label">IVA</p>
                <p className="info-value">${factura.iva?.toFixed(2)}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Items de la factura */}
      <div className="factura-items">
        <div className="items-header">
          <h2>Detalle de Items</h2>
        </div>

        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th className="text-right">Cantidad</th>
                <th className="text-right">P.Unitario</th>
                <th className="text-right">Dto %</th>
                <th className="text-right">Subtotal</th>
                <th className="text-right">IVA</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {factura.items.map((item, index) => (
                <tr key={item.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td className="item-descripcion">{item.descripcion}</td>
                  <td className="text-right">{item.cantidad}</td>
                  <td className="text-right">${item.precio_unitario.toFixed(2)}</td>
                  <td className="text-right">{item.descuento}%</td>
                  <td className="text-right">${item.subtotal.toFixed(2)}</td>
                  <td className="text-right">${item.iva.toFixed(2)}</td>
                  <td className="text-right font-medium">${item.total.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div className="items-totales">
          <div className="totales-container">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">${factura.subtotal?.toFixed(2)}</span>
            </div>
            <div className="total-row descuento">
              <span className="total-label">Descuento:</span>
              <span className="total-value">-${factura.descuento?.toFixed(2)}</span>
            </div>
            <div className="total-row iva">
              <span className="total-label">IVA (15%):</span>
              <span className="total-value">${factura.iva?.toFixed(2)}</span>
            </div>
            <div className="total-row grand-total">
              <span className="total-label">TOTAL:</span>
              <span className="total-value">${factura.total?.toFixed(2)}</span>
            </div>
            {factura.saldo_pendiente > 0 && factura.estado !== 'PAGADA' && (
              <div className="total-row saldo">
                <span className="total-label">Saldo pendiente:</span>
                <span className="total-value">${factura.saldo_pendiente?.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Observaciones */}
      <div className="factura-observaciones">
        <h3>Observaciones</h3>
        {editMode ? (
          <textarea
            value={editData.observaciones}
            onChange={(e) => setEditData({...editData, observaciones: e.target.value})}
            className="observaciones-textarea"
            rows="4"
            placeholder="Agregar observaciones..."
          />
        ) : (
          <div className="observaciones-contenido">
            {factura.observaciones || 'Sin observaciones'}
          </div>
        )}
      </div>
    </div>
  );
}
